import {
  resolveIndexCollectionHeading,
  useIndexSection,
} from '@/components/page-sections/index-section';
import { IndexCollectionBlocks } from '@/components/page-sections/index-section-collection-blocks';
import {
  readConstrainedIndexLayoutProps,
  readIndexSectionViewVariant,
} from '@/components/page-sections/index-section-utils';
import { IndexSectionView } from '@/components/page-sections/index-section-views';
import {
  usePageSectionContext,
  usePageSectionData,
} from '@/components/page-sections/page-section-context';
import { EmptyState } from '@/components/shared/empty-state';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { blogToCollectionItem } from '@/lib/blocks/collection/item-mappers/blog';
import type { BlogRecord } from '@/lib/blocks/collection/records';
import { type BlogIndexProps, BlogIndexVariant } from './definition';
import { readBlogIndexProps } from './display-options';
import { normalizeBlog, resolveBlogFilterTopics } from './normalizers';
import { BlogPinnedChrome } from './pinned-chrome';
import { TEMPLATE_PREVIEW_BLOGS } from './preview-data';

const BLOG_INDEX_COLLECTION_VARIANTS = {
  list: BlogIndexVariant.LIST_LAYOUT,
  grid: BlogIndexVariant.GRID_LAYOUT,
  split: BlogIndexVariant.SPLIT_LAYOUT,
};
const BLOG_COLLECTION_HEADING = 'All Posts';

function indexCollectionImagePosition(variant: string) {
  return variant === BlogIndexVariant.LIST_LAYOUT ? 'left' : 'top';
}

function hasLiveBlogIdentity(blog: BlogRecord | null | undefined) {
  return Boolean(blog?.pageId);
}

function hasBlogImage(blog: BlogRecord | null | undefined) {
  return Boolean(blog?.image?.url || blog?.imageUrl);
}

export function BlogIndexSection() {
  const { props, variant = BlogIndexVariant.GRID_LAYOUT } = usePageSectionData<BlogIndexProps>();
  const rawSectionContext = usePageSectionContext();
  const isPreview = useIsPreviewMode();
  const isStaticPreview = rawSectionContext ? rawSectionContext.renderMode !== 'default' : false;
  const isLayoutPreview = rawSectionContext?.renderMode === 'layout_preview';
  const sectionProps = readBlogIndexProps(props);
  const collectionHeading = resolveIndexCollectionHeading(BLOG_COLLECTION_HEADING);
  const sectionBlogs = sectionProps.blogs?.data ?? [];
  const controllerInput = {
    filterOrdering: sectionProps.filterOrdering,
    backgroundProps: sectionProps.backgroundProps,
    pagination: sectionProps.blogs?.pagination,
  };
  const hasResolvedBlogs = isLayoutPreview
    ? false
    : isStaticPreview
      ? sectionBlogs.some((blog) => hasLiveBlogIdentity(normalizeBlog(blog)))
      : sectionBlogs.length > 0;
  const usesPreviewBlogs = isStaticPreview && !hasResolvedBlogs;
  const resolvedPinnedBlogs = isLayoutPreview
    ? [TEMPLATE_PREVIEW_BLOGS[0]].map(normalizeBlog)
    : Array.isArray(sectionProps.blogs?.pinned)
      ? sectionProps.blogs.pinned.map(normalizeBlog)
      : Array.isArray(sectionProps.pinned)
        ? sectionProps.pinned.map(normalizeBlog)
        : [];
  const blogsList =
    hasResolvedBlogs || !isStaticPreview ? sectionBlogs : TEMPLATE_PREVIEW_BLOGS.slice(1);
  const normalizedBlogs = blogsList.map(normalizeBlog);
  const blogsForFilters = [...resolvedPinnedBlogs, ...normalizedBlogs];
  const topics = resolveBlogFilterTopics({
    sectionTopics: sectionProps.topics,
    blogs: blogsForFilters,
    isTemplatePreview: usesPreviewBlogs,
  });

  const {
    sortedFilters: sortedTopics,
    hasRecords: hasBlogs,
    shouldShowPinned,
    pinnedRecords,
    visibleRecords: visibleBlogs,
    hasVisibleRecords: hasVisibleBlogs,
    sectionBackgroundColor,
    pinnedCollectionBlocks,
    listCollectionBlocks,
    filterBlock,
    hasPinnedCollectionBlocks,
    hasListCollectionBlocks,
    paginationNode,
  } = useIndexSection({
    ...controllerInput,
    records: normalizedBlogs,
    pinnedRecords: resolvedPinnedBlogs,
    filters: topics,
  });
  const layoutProps = readConstrainedIndexLayoutProps(sectionProps.backgroundProps);

  if (!hasBlogs && !shouldShowPinned) {
    return <EmptyState message="No blogs available. Add blogs to display them here." />;
  }

  const renderCollectionBlocks = (
    blogs: BlogRecord[],
    blocks: typeof pinnedCollectionBlocks,
    applyListImagePosition = false,
  ) => (
    <IndexCollectionBlocks
      blocks={blocks}
      records={blogs}
      sectionId={rawSectionContext?.sectionId}
      editorSectionId={rawSectionContext?.editorSectionUiKey}
      isStaticPreview={isStaticPreview}
      isPreview={isPreview}
      variant={variant}
      contentType="blog"
      variants={BLOG_INDEX_COLLECTION_VARIANTS}
      toCollectionItem={blogToCollectionItem}
      extraVariantProps={
        applyListImagePosition
          ? (variant) => ({ imagePosition: indexCollectionImagePosition(variant) })
          : undefined
      }
    />
  );
  const renderedPinnedCollectionBlocks = renderCollectionBlocks(
    pinnedRecords,
    pinnedCollectionBlocks,
  );
  const pinnedOverlayHasImage =
    variant === BlogIndexVariant.LIST_LAYOUT && pinnedRecords.some(hasBlogImage);
  const pinnedCollectionBody = (
    <BlogPinnedChrome
      pinnedHeading={sectionProps.pinnedHeading}
      pinnedDescription={sectionProps.pinnedDescription}
    >
      {renderedPinnedCollectionBlocks}
    </BlogPinnedChrome>
  );

  return (
    <IndexSectionView
      variant={readIndexSectionViewVariant(variant)}
      layoutProps={layoutProps}
      sectionBackgroundColor={sectionBackgroundColor}
      filters={{
        show: sectionProps.showFilters,
        limit: sectionProps.filterLimit,
        items: sortedTopics,
        key: 'topics',
        label: 'Topics',
        block: filterBlock,
      }}
      pinnedCollection={{
        show: shouldShowPinned,
        hasCollectionBlocks: hasPinnedCollectionBlocks,
        body: pinnedCollectionBody,
        presentation: pinnedOverlayHasImage ? 'featuredOverlay' : undefined,
      }}
      collection={{
        hasVisibleRecords: hasVisibleBlogs,
        hasCollectionBlocks: hasListCollectionBlocks,
        heading: collectionHeading,
        body: renderCollectionBlocks(visibleBlogs, listCollectionBlocks, true),
        pagination: paginationNode,
      }}
    />
  );
}
